import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Fade,
  Skeleton,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import { Markdown } from '@summer-health/shared/content/react';
import { isNanitContentfulTitle } from '@summer-health/shared/utils';
import { useRouteChangeVisibility } from '@summer-health/ui/care-animations';
import { FullScreenImageWithChildren } from '@summer-health/ui/care-interstitial';
import { NextLinkComposed } from '@summer-health/ui/next-link-composed';
import { useEffect, useState } from 'react';
import {
  usePartnershipWelcomeContentQuery,
  WelcomeCopy,
} from '../../queries/partnership/partnership-welcome-content-query';

export const PartnershipWelcomeCard = ({
  content,
  agreeUrl,
}: {
  content: WelcomeCopy;
  agreeUrl: string;
}): JSX.Element => {
  const isNanit = isNanitContentfulTitle(content.name);
  return (
    <Card variant="standard">
      <CardHeader
        title={
          content.title ? (
            <>
              {!isNanit && (
                <Typography variant="body2" color="sunrise.text-secondary">
                  {content.name}
                </Typography>
              )}
              <Typography variant="h3">{content.title}</Typography>
            </>
          ) : (
            <>
              <Skeleton
                variant="text"
                sx={(theme): object => ({
                  backgroundColor: 'var(--mui-sunrise-light-text-disabled)',
                  marginBottom: theme.spacing(1),
                  width: '20%',
                })}
                height={6}
              />
              <Skeleton
                variant="text"
                sx={(theme): object => ({
                  backgroundColor: 'var(--mui-sunrise-light-text-primary)',
                  marginBottom: theme.spacing(1),
                  width: '50%',
                })}
                height={6}
              />
            </>
          )
        }
      />
      <CardContent>
        {content.welcomePageContent ? (
          <Markdown content={content.welcomePageContent} />
        ) : (
          <Stack spacing={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={`skeleton-text-${i}`}
                variant="text"
                sx={(theme): object => ({
                  backgroundColor: 'var(--mui-sunrise-light-text-disabled)',
                  marginBottom: theme.spacing(1),
                })}
                height={6}
              />
            ))}
          </Stack>
        )}

        <Divider variant="standard" sx={{ marginTop: 2.5 }} />
      </CardContent>
      <CardActions>
        <Button
          color="primary"
          sx={{ width: { xs: '100%', md: 'auto' } }}
          component={NextLinkComposed}
          to={agreeUrl}
        >
          {content.welcomeCta}
        </Button>
      </CardActions>
    </Card>
  );
};

export function PartnershipWelcomePage({
  slug,
}: {
  slug: string;
}): JSX.Element {
  const { content } = usePartnershipWelcomeContentQuery(slug);
  const { visible } = useRouteChangeVisibility();

  const [agreeUrl, setAgreeUrl] = useState<string>(
    `/signup/agree?returnTo=/welcome/${slug}/verify`,
  );

  useEffect(() => {
    if (!content) return;

    if (content.url) {
      setAgreeUrl(content.url);
      return;
    }

    const returnToParams = new URLSearchParams(window?.location?.search);
    returnToParams.delete('token');
    returnToParams.delete('signature');

    const returnTo = `/welcome/${slug}/verify?${returnToParams.toString()}`;

    const params = new URLSearchParams(window?.location?.search);
    params.append('partnershipSlug', slug);
    params.append('returnTo', returnTo);
    setAgreeUrl(`/signup/agree?${params.toString()}`);
  }, [content, slug]);

  return (
    <Container
      maxWidth="sm"
      sx={(theme) => ({
        [theme.breakpoints.up('md')]: {
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        },
      })}
    >
      {content.splashMedia?.url ? (
        <Fade in={visible} timeout={1000}>
          <div>
            <FullScreenImageWithChildren imgSrc={content.splashMedia.url}>
              <Slide in={visible} direction="up" timeout={1000}>
                <div>
                  <PartnershipWelcomeCard
                    content={content}
                    agreeUrl={agreeUrl}
                  />
                </div>
              </Slide>
            </FullScreenImageWithChildren>
          </div>
        </Fade>
      ) : (
        <Slide in={visible} direction="up" timeout={1000}>
          <div>
            <PartnershipWelcomeCard content={content} agreeUrl={agreeUrl} />
          </div>
        </Slide>
      )}
    </Container>
  );
}
